import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import {
  LayoutDashboard,
  Building2,
  Cog,
  Wrench,
  AlertTriangle,
  LogOut,
  Menu,
  ChevronRight,
  Users,
  CalendarDays,
  FileText,
  Receipt,
  Clock
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_fixtrack-25/artifacts/fon8nfqi_Disen%CC%83o%20sin%20ti%CC%81tulo.png";

const adminNavItems = [
  { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/admin/calendar', label: 'Calendario', icon: CalendarDays },
  { path: '/admin/companies', label: 'Empresas', icon: Building2 },
  { path: '/admin/machines', label: 'Máquinas', icon: Cog },
  { path: '/admin/works', label: 'Trabajos', icon: Wrench },
  { path: '/admin/incidents', label: 'Incidencias', icon: AlertTriangle },
  { path: '/admin/technicians', label: 'Técnicos', icon: Users },
  { path: '/admin/timelogs', label: 'Fichajes', icon: Clock },
  { path: '/admin/invoices', label: 'Facturas', icon: Receipt },
  { path: '/admin/reports', label: 'Informes', icon: FileText },
];

const techNavItems = [
  { path: '/tech', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/tech/works', label: 'Registrar Trabajo', icon: Wrench },
  { path: '/tech/incidents', label: 'Ver Incidencias', icon: AlertTriangle },
  { path: '/tech/timeclock', label: 'Control Horario', icon: Clock },
];

const clientNavItems = [
  { path: '/client', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/client/machines', label: 'Mis Máquinas', icon: Cog },
  { path: '/client/works', label: 'Trabajos Realizados', icon: Wrench },
  { path: '/client/incidents', label: 'Incidencias', icon: AlertTriangle },
  { path: '/client/invoices', label: 'Mis Facturas', icon: Receipt },
  { path: '/client/reports', label: 'Informes', icon: FileText },
];

const getRoleLabel = (role) => {
  const labels = {
    admin: 'Administrador',
    technician: 'Técnico',
    client: 'Cliente'
  };
  return labels[role] || role;
};

export const Layout = ({ children, title }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const navItems = user?.role === 'admin' 
    ? adminNavItems 
    : user?.role === 'technician' 
      ? techNavItems 
      : clientNavItems;
  
  return (
    <div className="min-h-screen bg-[#020617] flex">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-[#0F172A] border-r border-slate-800
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-800">
            <Link to={user?.role === 'admin' ? '/admin' : user?.role === 'technician' ? '/tech' : '/client'} className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Navalec" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="font-rajdhani text-xl font-bold text-white tracking-tight">NAVALEC</h1>
                <p className="text-xs text-sky-400 font-medium tracking-widest">SOLUTIONS</p>
              </div>
            </Link>
          </div>
          
          {/* Navigation */}
          <ScrollArea className="flex-1 p-4">
            <nav className="space-y-1">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-sm text-sm font-medium
                      transition-all duration-200 group
                      ${isActive 
                        ? 'bg-sky-500/10 text-sky-400 border-l-2 border-sky-400' 
                        : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
                      }
                    `}
                  >
                    <Icon className={`w-5 h-5 ${isActive ? 'text-sky-400' : 'text-slate-500 group-hover:text-sky-400'}`} />
                    <span>{item.label}</span>
                    {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>
          </ScrollArea>
          
          {/* User info */}
          <div className="p-4 border-t border-slate-800">
            <div className="flex items-center gap-3 px-4 py-3 rounded-sm bg-slate-800/30">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                user?.role === 'admin' ? 'bg-sky-500/20' : 
                user?.role === 'technician' ? 'bg-emerald-500/20' : 'bg-amber-500/20'
              }`}>
                <span className={`font-semibold text-sm ${
                  user?.role === 'admin' ? 'text-sky-400' : 
                  user?.role === 'technician' ? 'text-emerald-400' : 'text-amber-400'
                }`}>
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                <p className="text-xs text-slate-500 truncate">
                  {user?.role === 'client' ? user?.company_name : getRoleLabel(user?.role)}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={logout}
              className="w-full mt-2 text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 justify-start gap-3"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-[#020617]/80 backdrop-blur-xl border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-slate-400"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <h2 className="text-2xl font-rajdhani font-bold text-white tracking-tight uppercase">
              {title}
            </h2>
          </div>
        </header>
        
        {/* Page content */}
        <div className="flex-1 p-6 overflow-auto">
          {children}
        </div>
      </main>
    </div>
  );
};
